import React, { useState, useEffect } from 'react';
import { getUniqueOptions } from '../../../utils/classworkUtils';
import GoalList from '../../../components/Classroom/GoalList';
import GoalModal from '../../../components/Classroom/GoalModal';
import GoalProgress from '../../../components/Classroom/GoalProgress';
import '../../../css/Classroom.css';
import { createGoal } from '../../../services/GoalService.ts';
import { getStudentProgress } from '../../../services/ClassroomService.ts';
import { useData } from '../../../state/DataContext';

const ClassworkTab = ({ classroomDetails }) => {
  const { basicData, advancedData } = useData();
  const { goalsData, id: classroomId } = classroomDetails;

  const [goals, setGoals] = useState(goalsData || []);
  const [studentProgress, setStudentProgress] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [goalForm, setGoalForm] = useState({ name: '', section: '', courseType: '' });
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [progressFilters, setProgressFilters] = useState({ email: '', status: '' });

  useEffect(() => {
    const fetchStudentProgress = async () => {
      if (classroomId) {
        try {
          setLoadingProgress(true);
          const progress = await getStudentProgress(classroomId);
          setStudentProgress(progress);
        } catch (error) {
          console.error("Failed to fetch student progress", error);
        } finally {
          setLoadingProgress(false);
        }
      }
    };
    fetchStudentProgress();
  }, [classroomId]);

  const sectionOptions = getUniqueOptions(studentProgress, 'sectionId', 'Section');
  const courseTypeOptions = getUniqueOptions(studentProgress, 'courseType', 'Course Type');
  const emailOptions = getUniqueOptions(studentProgress, 'email', 'Email');
  const statusOptions = [
    { value: '', label: 'Status' },
    { value: 'completed', label: 'Completed' },
    { value: 'in-progress', label: 'In Progress' },
  ];

  const openModal = () => setShowModal(true);
  const closeModal = () => {
    setShowModal(false);
    setGoalForm({ name: '', section: '', courseType: '' });
  };

  const handleGoalFormChange = (e) => {
    const { name, value } = e.target;
    setGoalForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!goalForm.name.trim() || !goalForm.section || !goalForm.courseType) return;

    const sectionId = goalForm.section; 

    if (!sectionId) {
        console.error("Could not find section ID for:", goalForm.section);
        return;
    }

    try {
      const newGoal = await createGoal(classroomId, goalForm.name.trim(), sectionId, goalForm.courseType);
      setGoals(prev => [...prev, newGoal]);
      closeModal();
    } catch (err) {
      console.error("Error creating goal:", err);
    }
  };

  const handleProgressFilterChange = (e) => {
    const { name, value } = e.target;
    setProgressFilters(prev => ({ ...prev, [name]: value }));
  };

  let filteredStudents = [];
  if (selectedGoal) {
    filteredStudents = studentProgress.filter(
      s => s.sectionId === selectedGoal.sectionId && s.courseType === selectedGoal.courseType
    );
    if (progressFilters.email) {
      filteredStudents = filteredStudents.filter(s => s.email === progressFilters.email);
    }
    if (progressFilters.status) {
      const statusBool = progressFilters.status === 'completed';
      filteredStudents = filteredStudents.filter(s => s.status === statusBool);
    }
  }

  return (
    <div className="classwork-tab" style={{ maxWidth: 1600, margin: '0 auto', padding: '1rem', border: '0px solid black' }}>
      {!selectedGoal ? (
        <>
          <h2 style={{ fontWeight: 700, fontSize: '1.5rem', margin: '1rem 0' }}>Classwork Goals</h2>
          <button className="add-goal-btn" onClick={openModal}>+ Add Goal</button>
          {goals.length === 0
            ? <div className="no-goals">No goals have been added yet. Click 'Add Goal' to get started.</div>
            : <GoalList goals={goals} onSelectGoal={setSelectedGoal} />}
          <GoalModal
            show={showModal}
            onClose={closeModal}
            onSubmit={handleAddGoal}
            form={goalForm}
            onChange={handleGoalFormChange}
            sectionOptions={sectionOptions}
            courseTypeOptions={courseTypeOptions}
          />
        </>
      ) : (
        <GoalProgress
          selectedGoal={selectedGoal}
          progressFilters={progressFilters}
          onFilterChange={handleProgressFilterChange}
          emailOptions={emailOptions}
          statusOptions={statusOptions}
          filteredStudents={filteredStudents}
          onBack={() => { setSelectedGoal(null); setProgressFilters({ email: '', status: '' }); }}
          loading={loadingProgress}
        />
      )}
    </div>
  );
};

export default ClassworkTab;